import { MotionGlobalConfig } from 'framer-motion';
import { preloadRoute } from '@/lib/routePrefetch';

/**
 * Static → live handoff for prerendered pages (2026-09-24).
 *
 * scripts/prerender.mjs snapshots the public pages at deploy time into
 * `<div id="root" data-prerendered="/path">…</div>`, so the page is readable
 * before any JavaScript runs. When the app starts on such a page it does NOT
 * paint over the snapshot. It renders into a second, hidden container, waits
 * until that one holds the page with every entrance animation finished, then
 * swaps it in — in one frame, at the same scroll position. The visitor sees
 * the static page turn interactive with no blink, no fade replay and no jump.
 *
 * During the hidden phase (see index.css "Static handoff"):
 *   .handoff-hidden  — CSS transitions off, so fades land instantly; removed
 *                      at the swap (a finished transition never replays).
 *   .handoff-eager   — .cin-eager entrance animations take 1ms; kept until the
 *                      first navigation, because restoring their duration on a
 *                      still-mounted element would replay them mid-way.
 *   MotionGlobalConfig.skipAnimations — framer-motion lands instantly too.
 *
 * index.html is also the SPA fallback for every path that has no file of its
 * own, so it can carry the prerendered homepage for, say, /product/x. The
 * snippet prerender.mjs puts in <head> hides #root when the path doesn't
 * match; here the stale snapshot is cleared and the app renders normally.
 */

export const normalizePath = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path) || '/';

const MAX_WAIT_MS = 4000;       // for the route chunk, and again for the live page's content
const CONTENT_RATIO = 0.85;     // live text vs. snapshot text before we swap

let handoffPath: string | null = null;
let scrollSkipConsumed = false;

let markLive: () => void = () => {};
const livePromise = new Promise<void>((resolve) => {
  markLive = resolve;
});

/**
 * Resolves once the visitor is looking at the live app: right away when there
 * was no prerendered page to take over, otherwise at the swap. For UI that
 * runs its own timer from the moment it is raised — a toast raised on mount
 * would otherwise spend its 5 seconds inside the hidden live tree and be gone
 * by the time the page is swapped in (seen on /shop?removed=1, 2026-10-07).
 */
export const whenLive = () => livePromise;

/**
 * One-shot: true only the first time it's asked for the handoff page.
 * PremiumLayout asks from its scroll-to-top effect, which would otherwise
 * yank a visitor who already scrolled the static page back to the top.
 */
export function consumeHandoffScrollSkip(pathname: string): boolean {
  if (scrollSkipConsumed || handoffPath === null || normalizePath(pathname) !== handoffPath) return false;
  scrollSkipConsumed = true;
  return true;
}

/** Called on the first client-side navigation: later pages get their entrance animations back. */
export function endHandoffEager() {
  document.getElementById('root')?.classList.remove('handoff-eager');
}

const textLength = (el: Element) => (el.textContent || '').replace(/\s+/g, '').length;
const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Wait until images in the first screen of the live page are decoded, so none flashes empty. */
async function decodeVisibleImages(root: HTMLElement) {
  const viewportBottom = window.scrollY + window.innerHeight;
  const images = Array.from(root.querySelectorAll('img')).filter((img) => {
    const rect = img.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    return top < viewportBottom && top + rect.height > window.scrollY;
  });
  await Promise.race([
    Promise.all(images.map((img) => img.decode().catch(() => undefined))),
    delay(1000),
  ]);
}

type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const defaultValueOf = (control: Control) =>
  control instanceof HTMLSelectElement
    ? (Array.from(control.options).find((option) => option.defaultSelected) ?? control.options[0])?.value ?? ''
    : control.defaultValue;

/**
 * Carry over anything the visitor typed or ticked on the static page (e.g. a
 * phone number on /repair-status typed before the app is ready). Controls are
 * matched by name, else by position. Returns a function that moves the focus
 * (and caret) across too — to be called once the live page is visible, since
 * a visibility:hidden element can't take focus.
 */
function transferFormState(from: HTMLElement, to: HTMLElement): () => void {
  let restoreFocus = () => {};
  const selector = 'input, textarea, select';
  const sources = Array.from(from.querySelectorAll<Control>(selector));
  const targets = Array.from(to.querySelectorAll<Control>(selector));
  const active = document.activeElement;

  sources.forEach((source, index) => {
    const byName = source.name ? to.querySelector<Control>(`[name="${CSS.escape(source.name)}"]`) : null;
    const target = byName || targets[index];
    if (!target || target.tagName !== source.tagName || target.type !== source.type) return;

    if (source instanceof HTMLInputElement && (source.type === 'checkbox' || source.type === 'radio')) {
      if (source.checked !== source.defaultChecked && (target as HTMLInputElement).checked !== source.checked) {
        (target as HTMLInputElement).click();
      }
    } else if (source.value !== defaultValueOf(source) && target.value !== source.value) {
      // Go through the native setter so React's onChange sees the new value.
      const proto = Object.getPrototypeOf(target);
      Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(target, source.value);
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (source === active) {
      const selection = source instanceof HTMLSelectElement ? null : [source.selectionStart, source.selectionEnd] as const;
      restoreFocus = () => {
        target.focus({ preventScroll: true });
        if (selection && selection[0] !== null && !(target instanceof HTMLSelectElement)) {
          try { target.setSelectionRange(selection[0], selection[1]); } catch { /* not a text control */ }
        }
      };
    }
  });
  return restoreFocus;
}

/**
 * Mount the app. `render(container, onCommit)` must render into `container`
 * and call `onCommit` once React has committed for the first time.
 */
export async function startApp(render: (container: HTMLElement, onCommit: () => void) => void) {
  const staticRoot = document.getElementById('root')!;
  const snapshotPath = staticRoot.getAttribute('data-prerendered');
  const here = normalizePath(window.location.pathname);

  if (!snapshotPath || snapshotPath !== here) {
    if (snapshotPath) {
      // SPA fallback carrying the homepage snapshot for another path.
      staticRoot.replaceChildren();
      staticRoot.removeAttribute('data-prerendered');
      document.documentElement.classList.remove('prerender-mismatch');
    }
    render(staticRoot, () => {});
    markLive();
    return;
  }

  handoffPath = here;
  await Promise.race([preloadRoute(here), delay(MAX_WAIT_MS)]);

  // Take the snapshot out of the way of the live tree: ids move to data-static-id
  // (no duplicates for getElementById, anchors or aria references) and the
  // container gives up id="root".
  staticRoot.id = 'root-static';
  staticRoot.querySelectorAll('[id]').forEach((el) => {
    el.setAttribute('data-static-id', el.id);
    el.removeAttribute('id');
  });

  const live = document.createElement('div');
  live.id = 'root';
  live.className = 'handoff-hidden handoff-eager';
  live.setAttribute('aria-hidden', 'true');
  live.style.cssText = 'position:absolute;top:0;left:0;right:0;visibility:hidden;';
  staticRoot.after(live);

  MotionGlobalConfig.skipAnimations = true;
  let restoreFocus = () => {};
  try {
    // Every wait is capped: whatever happens, the live page is swapped in.
    await Promise.race([new Promise<void>((resolve) => render(live, resolve)), delay(MAX_WAIT_MS)]);

    // Wait for the page's content (route data, nested lazy parts) to be there.
    const target = textLength(staticRoot) * CONTENT_RATIO;
    const started = performance.now();
    while (textLength(live) < target && performance.now() - started < MAX_WAIT_MS) {
      await delay(100);
    }
    await decodeVisibleImages(live);
    await nextFrame();
    await nextFrame();
    restoreFocus = transferFormState(staticRoot, live);
  } finally {
    staticRoot.remove();
    live.removeAttribute('style');
    live.removeAttribute('aria-hidden');
    // Apply "visible" while transitions are still off: visibility is inherited
    // and animatable, so with `transition-all` on the page wrapper it would
    // otherwise stay hidden for a frame — a blank flash right at the swap.
    void live.offsetHeight;
    live.classList.remove('handoff-hidden');
    MotionGlobalConfig.skipAnimations = false;
    restoreFocus();
    markLive();
  }
}
